import axios from "axios";
import { API } from "../../../config";

import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";

import { setSelectionModelUsers } from "../../slices/users/users";
import {
  LocalizedTextGrouping,
  OrgGroupTypeIds,
  Scopes,
  UserData,
} from "./userTypes";
import { setFiltersConfig } from "../../slices/userGroups/userGroupsSliceMultipleTables";
import { addHours, setZeroHrs } from "../dashboard/dashboardAsyncActions";
import { moveScenarioToGroup } from "../actions/actionsAsyncActions";
import {
  setMultiLocationSuccess,
  setMultiLocsCreated,
} from "../../slices/location/createLocation";
import { LocationGrainTrackerValues } from "../../slices/grainTracker/grainTrackerTypes";
import { fetchLocations } from "../locations/locationsAsyncActions";
import { ScenarioData } from "../actions/actionTypes";
import { getLocalizedTextByLocaleVersionTwo } from "../../../utils/localized-text";

export const fetchScenarios = async (
  token: string,
  orgId: number,
  locale: string,
): Promise<any> => {
  const res = await axios({
    method: "GET",
    url: `${API}/core/scenario?isSystemScenario=false&deletedAt=null&statusConstantId[$in][]=34&statusConstantId[$in][]=35&$eager=[scopes.[group], cerealType.text.[localizedTexts.[locale]],statusConstant, name.[localizedTexts.[locale]], desc.[localizedTexts.[locale]]]&$modifyEager[scopes.scopeGroupId][$ne]=null&$modifyEager[scopes.deletedAt]=null&$modifyEager[scopes.group][orgId]=${orgId}`,
    headers: {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${token}`,
    },
  });
  const { data } = res;
  return data?.data ?? [];
};

export const fetchManagerGeoPoints = async (
  token: string,
  groupsId: number[],
  locale: string,
): Promise<any> => {
  const queryString = groupsId.map((id) => `groupId[$in][]=${id}`).join("&");
  const res = await axios({
    method: "GET",
    url: `${API}/users/organization-group-membership?${queryString}&deletedAt=null&geoPointId[$ne]=null`,
    headers: {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${token}`,
    },
  });
  const { data } = res;
  return data?.data ?? [];
};

export const fetchUsersNewRequest = createAsyncThunk(
  "UsersReducer/fetchUsersNewRequest",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { orgId, user, username } = state.Authentication;
    const { locale } = state.LayoutReducer;
    const { locations } = state.LocationsReducer;
    const { usersSBFilters, filterModel } = state.CreateTablesUsersReducer;

    const { filtersRoles, filtersLocation, filterActions, filterCreationDate } =
      usersSBFilters;

    const locationFilter = filterModel.items.find(
      (item) => item.field === "assignedSite",
    )?.value;

    const dateFilter = usersSBFilters.CreationDateSelected.filter(
      (value) => value.isSelected === true,
    ).map((x) => `createdAt[$gte]=${addHours(setZeroHrs(x.id)).toISOString()}`);

    const lastDateFilter = dateFilter[dateFilter.length - 1];

    const queryParams = [
      "$limit=10000",
      "$eager=[groups, groupMemberships, associatedGeoPoint]",
      "$modifyEager[groups.deletedAt]=null",
      locationFilter,
      !lastDateFilter ? "" : lastDateFilter,
    ].join("&");

    const filtersCount = [
      filtersRoles,
      filtersLocation,
      filterActions,
      filterCreationDate,
    ];

    const filtersSelectedAfterFetch = filtersCount
      .filter((elem) => !!elem)
      .map((x) => x!.value.length)
      .reduce((p, c) => p + c, 0);

    store.dispatch(
      setFiltersConfig({
        ...usersSBFilters,
        filtersSelectedAfterFetch,
      }),
    );

    try {
      const rawScenarios = await fetchScenarios(
        user!.token,
        orgId as number,
        locale,
      );

      const response = await axios.get(`${API}/users/user?${queryParams}`, {
        headers: {
          "Accept-Language": `${locale}`,
          Authorization: `Bearer ${user!.token}`,
        },
      });
      const { data } = response;

      const rawUsers = data.data;

      const managerUsers = data.data.filter(
        (user: any) => user.userType === "real",
      );

      const managerLocations = await Promise.all(
        managerUsers.map((u: any) =>
          fetchManagerGeoPoints(
            user!.token,
            u.groups.map((g: any) => g.id),
            locale,
          ),
        ),
      );

      const myManagerLocationsIds = managerLocations.map((c, idx) =>
        c.total ? [...new Set(c.data.map((x: any) => x.geoPointId))] : [],
      ) as any[];

      managerUsers.forEach((elem: any, index: number) => {
        elem.myAssociatedGeoPoint = elem.associatedGeoPoint;
        elem.associatedGeoPoint = myManagerLocationsIds.find(
          (a: number[], i: number) => i === index,
        );
      });

      managerUsers.forEach((element: any, index: number) => {
        element.associatedGeoPoint = locations.filter((f) =>
          element.associatedGeoPoint.includes(f.id),
        );
        if (element.myAssociatedGeoPoint) {
          element.associatedGeoPoint.push(element.myAssociatedGeoPoint);
        }
      });

      let scenarios = rawScenarios.map(
        (x: {
          name: LocalizedTextGrouping;
          scopes: Scopes[];
          id: number;
          cerealType: any;
        }) => {
          return {
            id: x.id,
            actionId: x.id,
            name: x.name,
            scopes: x.scopes,
            cerealType: x.cerealType.text,
          };
        },
      );

      scenarios = scenarios.map((scenario: (typeof scenarios)[0]) => ({
        ...scenario,
        groups: scenario.scopes.map((x: any) => x.scopeGroupId),
      }));

      const usersMapped = rawUsers.map((user: UserData) => {
        const selfGroup = user.groups?.find((x) => x.typeId === 107);

        const actions = scenarios
          .filter((scenario: any) => scenario.groups?.includes(selfGroup?.id))
          .map((s: any) => ({ name: s.name, cerealType: s.cerealType }));

        return {
          createdAt: user.createdAt,
          updatedAt: user.updatedAt,
          id: user.id,
          username: user.username,
          email: user.email,
          emailVerifiedAt: user.emailVerifiedAt,
          firstname: user.firstname,
          lastname: user.lastname,
          lastAllotedDevice: user.lastAllotedDevice,
          deletedAt: user.deletedAt,
          isSuperAdmin: user.isSuperAdmin,
          userType: user.userType,
          associatedGeoPointId: user,
          remoteId: user.remoteId,
          metadata: user.metadata,
          attributeId: user.attributeId,
          associatedGeoPoint: Array.isArray(user?.associatedGeoPoint)
            ? user?.associatedGeoPoint
            : [user?.associatedGeoPoint],
          groups: user.groups.filter(
            (c) =>
              c.typeId === OrgGroupTypeIds.user ||
              c.typeId === OrgGroupTypeIds.geoPointCluster,
          ),
          rawGroups: user.groups,
          groupMemberships: user.groupMemberships,
          actions,
          actionsIds: actions.map((a: any) => a.name.id),

          scopesIds: scenarios
            .map((s: any) =>
              s.scopes
                .filter(
                  (x: any) => selfGroup && x.scopeGroupId == selfGroup?.id,
                )
                .map((x: any) => ({
                  scopeId: x.id,
                  scenarioId: x.scenarioId,
                  name: s.name,
                  cerealType: s.cerealType,
                })),
            )
            .flat(),
        };
      });

      const filterInternal = usersMapped.filter(
        (values) =>
          values.userType === "internal" && values.username !== username,
      );

      const removeData = usersMapped.filter((values) =>
        filterInternal.find((d) => d.username !== values.username),
      );

      const transformData = {
        total: data.total,
        limit: data.limit,
        skip: data.skip,
        data: filterInternal.length === 0 ? usersMapped : removeData,
      };

      return store.fulfillWithValue(transformData);
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const fetchGroups = createAsyncThunk(
  "UsersReducer/fetchGroups",
  async (token: string, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    try {
      const response = await axios.get(
        `${API}/users/organization-group?$limit=10000`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const { data } = response;

      return store.fulfillWithValue(data);
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const moveTogroup = async (
  token: string,
  userId: string,
  groupId: number,
  locale: string,
): Promise<UserData> => {
  const res = await axios({
    method: "POST",
    url: `${API}/users/group-membership`,
    data: { userId: userId, groupId: groupId },
    headers: {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${token}`,
    },
  });
  const { data } = res;
  return data;
};

export const moveAllGroup = createAsyncThunk(
  "UsersReducer/moveAllGroup",
  async (token: string, store) => {
    const { usersSelected, groupId } = (store.getState() as RootState)
      .UsersReducer;
    const { locale } = (store.getState() as RootState).LayoutReducer;
    try {
      const userPromises = usersSelected?.map((elt: string) => {
        const convert = elt.toString();
        return moveTogroup(token, convert, groupId, locale);
      });
      const move = await Promise.all(userPromises);

      store.fulfillWithValue(move);
      store.dispatch(fetchGroups(token));
      store.dispatch(fetchUsersNewRequest());
      store.dispatch(setSelectionModelUsers([]));
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const deleteSingleUserFromGroup = async (
  token: string,
  userId: string,
  groupId: number,
): Promise<UserData> => {
  const res = await axios({
    method: "DELETE",
    url: `${API}/users/group-membership?userId=${userId}&groupId=${groupId}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const { data } = res;
  return data;
};

export const deleteUserFromGroup = createAsyncThunk(
  "UsersReducer/deleteUserFromGroup",
  async (arg: { token: string; userId: string; groupId: number }, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    try {
      const response = await axios.delete(
        `${API}/users/group-membership?userId=${arg.userId}&groupId=${arg.groupId}`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${arg.token}`,
          },
        },
      );

      store.dispatch(fetchGroups(arg.token));
      store.dispatch(fetchUsersNewRequest());
      return store.fulfillWithValue(response);
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const deleteMultipleUsersFromGroup = createAsyncThunk(
  "UsersReducer/deleteMultipleUsersFromGroup",
  async (token: string, store) => {
    const { usersSelected, groupId } = (store.getState() as RootState)
      .UsersReducer;

    try {
      const userPromises = usersSelected.map((elt: string) => {
        const convert = elt.toString();
        return deleteSingleUserFromGroup(token, convert, groupId);
      });
      const remove = await Promise.all(userPromises);

      store.fulfillWithValue(remove);
      store.dispatch(fetchGroups(token));
      store.dispatch(fetchUsersNewRequest());
      store.dispatch(setSelectionModelUsers([]));
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const moveRemoveAddLocToUser = async (
  token: string,
  userId: string,
  geoPointId: number,
  method: string,
  locale: string,
): Promise<UserData> => {
  const dataInfo = () => {
    if (method === "addLoc" || method === "editLoc") {
      return geoPointId;
    }
    if (method === "removeLoc") {
      return null;
    }
  };

  const res = await axios({
    method: "PATCH",
    url: `${API}/users/user/${userId}`,
    headers: {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${token}`,
    },
    data: {
      associatedGeoPointId: dataInfo(),
    },
  });

  const { data } = res;
  return data;
};

export const linkUsersToLocationFinalStep = createAsyncThunk(
  "UserReducer/linkUsersToLocationFinalStep",
  async (
    arg: {
      locationGrainTrackerValues: LocationGrainTrackerValues;
      logOutGrainTracker: any;
    },
    store,
  ) => {
    const { userSelectedAutocompleteStep2NoUsers } = (
      store.getState() as RootState
    ).CreateLocationReducer;

    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;
    const token = user?.token ?? "";

    const usersSelected =
      userSelectedAutocompleteStep2NoUsers.valuesSelected.map(
        (values) => values.id,
      );

    if (!usersSelected.length) {
      store.dispatch(setMultiLocationSuccess(true));
      setTimeout(() => {
        store.dispatch(setMultiLocationSuccess(false));
        store.dispatch(arg.logOutGrainTracker());
      }, 200);
      return;
    }

    try {
      const userPromises = usersSelected.map((elt) => {
        return moveRemoveAddLocToUser(
          token,
          elt as string,
          arg.locationGrainTrackerValues.locationInformation?.id as number,
          "addLoc",
          locale,
        );
      });
      const move = await Promise.all(userPromises);

      if (move) {
        store.fulfillWithValue(move);
        store.dispatch(setMultiLocsCreated(1));
        store.dispatch(fetchGroups(token));
        store.dispatch(fetchUsersNewRequest());
        store.dispatch(setSelectionModelUsers([]));
        store.dispatch(setMultiLocationSuccess(true));
        store.dispatch(fetchLocations(token));
        setTimeout(() => {
          store.dispatch(setMultiLocationSuccess(false));
          store.dispatch(arg.logOutGrainTracker());
        }, 200);
      }
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const modifyAllUserToLoc = createAsyncThunk(
  "UserReducer/modifyAllUserToLoc",
  async (token: string, store) => {
    const { usersSelected, groupId, optionToModify } = (
      store.getState() as RootState
    ).UsersReducer;

    const { locale } = (store.getState() as RootState).LayoutReducer;

    try {
      const userPromises = usersSelected.map((elt: string) => {
        const convert = elt.toString();

        return moveRemoveAddLocToUser(
          token,
          convert,
          groupId,
          optionToModify,
          locale,
        );
      });
      const move = await Promise.all(userPromises);

      store.fulfillWithValue(move);
      store.dispatch(fetchGroups(token));
      store.dispatch(fetchUsersNewRequest());
      store.dispatch(setSelectionModelUsers([]));
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const userUpdateValues = createAsyncThunk(
  "CreateTablesUsersReducer/userUpdateValues",
  async (arg: { token: string; userId: string }, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { userValuesUpdate, selectedUser } = (store.getState() as RootState)
      .CreateTablesUsersReducer;
    const { site, contactEmail } = userValuesUpdate;
    const { groups } = selectedUser!;

    const copy = userValuesUpdate?.copyOfActions?.valuesSelected;
    const values = userValuesUpdate?.actions?.valuesSelected;
    const selfGroup = 24;

    const actionWithNoScope = values
      ?.map((x) => {
        if (x.scopeId === undefined) {
          return x;
        }
      })
      .filter(function (element) {
        return element !== undefined;
      });

    const actionAlreadyUsed = copy
      ?.map((x) => {
        if (actionWithNoScope.find((d) => d?.scopeId === x.scopeId)) {
          return x;
        }
      })
      .filter(function (element) {
        return element !== undefined;
      });

    const actionsToAdd = actionWithNoScope
      .map((non) => {
        if (actionAlreadyUsed.find((d) => d?.scopeId === non?.scopeId)) {
          return undefined;
        } else {
          return non;
        }
      })
      .filter(function (element) {
        return element !== undefined;
      });

    try {
      const response = await axios({
        method: "PATCH",
        url: `${API}/users/user/${arg.userId}`,
        headers: {
          "Accept-Language": `${locale}`,
          Authorization: `Bearer ${arg.token}`,
        },
        data: {
          associatedGeoPointId: site || undefined,
          email: contactEmail || undefined,
        },
      });

      const actionToDeletePromises = copy
        ?.map((copys) => {
          if (!values.find((x) => x.scopeId === copys.scopeId)) {
            return deleteScenarioFromUser(arg.token, copys.scopeId, locale);
          }
        })
        .filter(function (element) {
          return element !== undefined;
        });

      const moveActionsToUserGroupPromises = actionsToAdd
        .map((x) => {
          if (x?.scopeId === undefined && x?.scenarioId !== undefined) {
            return moveScenarioToGroup(
              arg.token,
              x.scenarioId,
              groups.find((c) => c.groupTemplateId === selfGroup)?.id,
              locale,
            );
          }
        })
        .filter(function (element) {
          return element !== undefined;
        });

      await Promise.all(actionToDeletePromises);
      await Promise.all(moveActionsToUserGroupPromises);

      store.dispatch(fetchUsersNewRequest());
      return store.fulfillWithValue(response);
    } catch (error: any) {
      return store.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const deleteScenarioFromUser = async (
  token: string,
  scopeId: string,
  locale: string,
): Promise<UserData> => {
  const res = await axios({
    method: "DELETE",
    url: `${API}/core/scenario-scope/${scopeId}`,
    headers: {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${token}`,
    },
  });

  const { data } = res;
  return data;
};

//// manager creation

export const getUserPassword = createAsyncThunk(
  "CreateTablesUsersReducer/getUserPassword",
  async (arg: { token: string; userId: string }, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;

    try {
      const response = await axios({
        method: "GET",
        url: `${API}/users/get-password/${arg.userId}`,
        headers: {
          "Accept-Language": `${locale}`,
          Authorization: `Bearer ${arg.token}`,
        },
      });

      return store.fulfillWithValue(response.data);
    } catch (error: any) {
      return store.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const fetchSBSites = createAsyncThunk(
  "CreateTablesUsersReducer/fetchSBSites",
  async (_, store) => {
    const { user } = (store.getState() as RootState).Authentication;
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const token = user?.token ?? "";
    try {
      const response = await axios.get(
        `${API}/users/organization-geo-point?$select[]=name&$limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": `${locale}`,
          },
        },
      );

      const { data } = response;

      const locations = data.data.map((loc) => ({
        id: loc.id,
        name: loc.name,
        totalCapacity: loc.totalCapacity,
        shared: loc.shared ?? false,
      }));

      const filterLoc = locations.filter((v) => v.shared !== true);

      return filterLoc;
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const fetchSBActionsNames = createAsyncThunk(
  "CreateTablesUsersReducer/fetchSBActionsNames",
  async (_, store) => {
    const { user } = (store.getState() as RootState).Authentication;
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const token = user?.token ?? "";
    try {
      const response = await axios.get(
        `${API}/core/scenario?isSystemScenario=false&deletedAt=null&statusConstantId[$in][]=34&statusConstantId[$in][]=35&$eager=[scopes.[group], cerealType.text.[localizedTexts.[locale]], statusConstant, name.[localizedTexts.[locale]], desc.[localizedTexts.[locale]]]&$limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": `${locale}`,
          },
        },
      );

      const { data } = response;

      const actionsValues = data.data.map((row: ScenarioData) => {
        const scenarioNameValue = () => {
          if (row.name) {
            let def = "";
            const arr = row.name.localizedTexts
              .map((c: any) => {
                if (locale === c.locale.value) {
                  return c.translation;
                }
                def = c.translation;
              })
              .filter((val: any) => null != val);
            if (!arr.length) {
              return [def].toString();
            } else {
              return arr.toString();
            }
          } else {
            return "Null";
          }
        };

        const tableRows = {
          name: scenarioNameValue().toLowerCase(),
          id: row.userFacingNameTextId,
          cerealType: getLocalizedTextByLocaleVersionTwo(
            locale,
            row.cerealType.text.localizedTexts,
          ),
        };
        return tableRows;
      });

      return actionsValues;
    } catch (error) {
      return error;
    }
  },
);
